import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { throttle } from 'lodash/fp';
import { Fade, Paper } from '@material-ui/core';

import { SCROLLABLE_CONTAINER } from '../../common/constants';
import messages from '../messages';
import StepGuideButton from './StepGuideButton';
import ProgressBar from './ProgressBar';

const S = {
  Wrapper: styled.div`
    position: fixed;
    bottom: 80px;
    user-select: none;
    @media (min-height: 872px) {
      bottom: auto;
    }
  `,
  StepGuideButton: styled(StepGuideButton)`
    margin-bottom: 16px;
  `,
  Paper: styled(Paper)`
    position: relative;
    width: 440px;
    padding: 32px 40px 24px;
    box-shadow: 0 12px 24px 0 rgba(0, 0, 0, 0.1);
  `,
  ProgressBar: styled(ProgressBar)`
    position: absolute;
    top: 0;
    left: 0;
  `,
};

const StepGuide = ({
  children,
  open,
  step,
  stepCount,
  onClick,
  onEnter,
  onExited,
}) => {
  const [scrollX, setScrollX] = useState(0);

  const rate = useMemo(() => (step ? (step - 1) / (stepCount - 1) : 0), [step, stepCount]);

  const handleScroll = useCallback(throttle(10, event => setScrollX(event.target.scrollLeft)), []);

  useEffect(() => {
    const scrollableContainer = document.querySelector(`#${SCROLLABLE_CONTAINER}`);

    if (scrollableContainer) {
      scrollableContainer.addEventListener('scroll', handleScroll);
    }

    return () => scrollableContainer.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <S.Wrapper style={{ transform: `translate(-${scrollX}px)` }}>
      <S.StepGuideButton type="button" active={open} onClick={onClick}>
        <FormattedMessage {...messages[open ? 'stepGuideButtonActive' : 'stepGuideButtonInactive']} />
      </S.StepGuideButton>
      <Fade
        in={open}
        onEnter={onEnter}
        onExited={onExited}
      >
        <S.Paper elevation={4}>
          <S.ProgressBar rate={rate} />
          {children}
        </S.Paper>
      </Fade>
    </S.Wrapper>
  );
};

StepGuide.propTypes = {
  children: PropTypes.node,
  open: PropTypes.bool.isRequired,
  step: PropTypes.number.isRequired,
  stepCount: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  onEnter: PropTypes.func,
  onExited: PropTypes.func,
};

StepGuide.defaultProps = {
  children: undefined,
  onEnter: () => {},
  onExited: () => {},
};

export default StepGuide;
